"use client";

import { type ReactNode, useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useLingui } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { Button, Section } from "@marketing-sites/ui/primitives";
import { cn } from "@marketing-sites/utils/cn";

function ArrowButton({
  direction,
  disabled,
  onClick,
  analyticsId,
}: {
  direction: "left" | "right";
  disabled: boolean;
  onClick: () => void;
  analyticsId: string;
}) {
  const { t } = useLingui();

  return (
    <Button
      analyticsId={analyticsId}
      variant="secondary"
      aria-label={direction === "left" ? t`Scroll left` : t`Scroll right`}
      onClick={onClick}
      disabled={disabled}
      className="px-0! size-10! not-disabled:cursor-pointer! cursor-default!"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        className={direction === "left" ? "rotate-180" : undefined}
      >
        <path
          d="M4 10h12m0 0-5-5m5 5-5 5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Button>
  );
}

export type ShippedFeaturesListProps = {
  className?: string;
  /**
   * Anchor id for the section. Defaults to a slug of `title`. Override when the
   * section's reason title (in `reasonHeader`) differs from `title`, so TOC jump
   * links resolve to the reason rather than the "shipped" heading.
   */
  id?: string;
  label?: string;
  title?: string;
  description?: string;
  /** Corner dot decorators on the section. Defaults to false. */
  dotDecorators?: boolean;
  /** Classes forwarded to the corner dots (e.g. `text-clay` for grey). */
  dotDecoratorsClassName?: string;
  /**
   * Optional reason header (numbered title + body + engraving) rendered above
   * the "shipped" heading, and an optional testimonial rendered below the
   * cards — both inside this section so the corner dots wrap the whole reason.
   */
  reasonHeader?: ReactNode;
  testimonial?: ReactNode;
  sectionMetadata: { analyticsId: string };
  scrollLeftButtonMetadata: { analyticsId: string };
  scrollRightButtonMetadata: { analyticsId: string };
} & (
  | { chapterName?: never; chapterMetadata?: never }
  | { chapterName: string; chapterMetadata: { analyticsId: string } }
);

export default function ShippedFeaturesList({
  className,
  id,
  label: labelProp,
  title: titleProp,
  description: descriptionProp,
  dotDecorators = false,
  dotDecoratorsClassName,
  reasonHeader,
  testimonial,
  chapterName,
  chapterMetadata,
  sectionMetadata,
  scrollLeftButtonMetadata,
  scrollRightButtonMetadata,
}: ShippedFeaturesListProps) {
  const { t } = useLingui();
  const label = labelProp ?? t`Helpdesk Updates`;
  const title =
    titleProp ?? t`Over 60 improvements to the helpdesk you use everyday`;
  const description =
    descriptionProp ??
    t`The features you've been asking for and improvements you'll notice every day.`;

  const categories = [
    {
      name: t`Inbox`,
      features: [
        t`Composer drafts`,
        t`Translation quality feedback`,
        t`Side conversation notifications`,
        t`Edit sent notes`,
        t`Unassign unsnoozed conversations`,
        t`Auto-unassign when away`,
        t`SLA sorting (FRT, NRT, TTC, TTR)`,
        t`Skip away mode in Round Robin`,
        t`Company pop-over with ID, copy, and open`,
        t`Side conversations export`,
        t`Bulk edit admin statuses in Realtime Dashboard`,
        t`Delete user notes`,
        t`Channel indicators on conversations`,
        t`Search in Views`,
        t`Duplicate a View`,
        t`Inbox assignment limits`,
        t`Conversation events UI updates`,
        t`Slack side conversations`,
        t`Invite configuration for team inboxes`,
        t`Delay send / undo send`,
      ],
    },
    {
      name: t`Phone`,
      features: [
        t`Call transfer to workflows`,
        t`Warm transfer to team`,
        t`CSAT for outbound calls`,
        t`Phone numbers for brands`,
        t`Recording consent (GDPR)`,
        t`Default outbound phone number`,
        t`Default outbound numbers for callbacks`,
        t`Quick reply stored on conversations`,
        t`Country-specific dial tones`,
        t`Enhanced call lifecycle and assignment visibility`,
        t`11 new call metrics`,
      ],
    },
    {
      name: t`Data Foundations`,
      features: [
        t`Data Connectors: pre-signed URLs`,
        t`Data Connectors: pre-configured OAuth providers`,
        t`Data Connectors: Execution Results API`,
        t`Data Connectors: health metrics`,
        t`Data Connectors: authentication improvements`,
        t`Data Connectors: action dependency observability`,
        t`Activity Log Event Types API`,
        t`Azure Blob Storage for conversation data export`,
        t`Shopify onboarding for new signups`,
        t`Public API v2.15`,
      ],
    },
    {
      name: t`And many more`,
      features: [
        t`Split delete permissions (replies vs notes)`,
        t`Macros permissions (create, edit, delete)`,
        t`Inbox permissions (trigger data connectors, reassign when away)`,
        t`Teammate profile permissions (edit own, edit others)`,
        t`Conversation permissions (participants, merge, creation)`,
        t`BPO permission restrictions (GDPR visibility control)`,
        t`Select ticket state when converting`,
        t`Change ticket type after creation`,
        t`Require ticket attribute on close`,
        t`Track ticket state transitions in API`,
        t`Tickets Portal: restrict user visibility`,
        t`Push notification management per device`,
        t`Holiday office hours`,
        t`Attachments improvements (bulk upload, carousel, grid)`,
        t`AI titles in Messenger`,
        t`Email recipients status`,
        t`Native Discord integration with Fin`,
        t`Scheduled external reports (GA)`,
        t`Ticket time to resolve in office hours`,
        t`Workspace avatars`,
        t`Disable team mentions at workspace level`,
        t`New macro actions: re-open conversation & set ticket state`,
        t`Conversation auto deletion`,
      ],
    },
  ];

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true,
    watchDrag: (_api, event) => event.type === "touchstart",
  });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollLeft(emblaApi.canScrollPrev());
    setCanScrollRight(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    updateScrollState();
    emblaApi.on("select", updateScrollState);
    emblaApi.on("reInit", updateScrollState);
    emblaApi.on("scroll", updateScrollState);
    return () => {
      emblaApi.off("select", updateScrollState);
      emblaApi.off("reInit", updateScrollState);
      emblaApi.off("scroll", updateScrollState);
    };
  }, [emblaApi, updateScrollState]);

  const showArrows = canScrollLeft || canScrollRight;

  return (
    <Section
      id={id ?? title.toLowerCase().replaceAll(" ", "-")}
      data-analytics-region="Shipped Features List"
      data-analytics-id={sectionMetadata.analyticsId}
      data-chapter-name={chapterName}
      data-chapter-analytics-id={chapterMetadata?.analyticsId}
      gridCols="10"
      className={cn("py-16 md:py-20", className)}
      dotDecorators={dotDecorators}
      dotDecoratorsClassName={dotDecoratorsClassName}
    >
      <div className="col-span-full grid grid-cols-subgrid gap-y-8 md:gap-y-10">
        {reasonHeader}
        <div className="col-span-full space-y-8">
          <div className="flex w-full justify-between">
            <div className="flex max-w-[70ch] flex-col gap-4">
              {label && <p className="text-label-lg">{label}</p>}
              <h2
                className={`text-balance ${label ? "text-heading-3" : "text-heading-4"}`}
              >
                {title}
              </h2>
              {description && (
                <p className="text-body-lg text-current/70 mt-2 max-w-2xl text-balance">
                  {description}
                </p>
              )}
            </div>
            {showArrows && (
              <div className="flex items-end justify-end pl-2">
                <div className="flex gap-2">
                  <ArrowButton
                    direction="left"
                    disabled={!canScrollLeft}
                    onClick={() => emblaApi?.scrollPrev()}
                    analyticsId={scrollLeftButtonMetadata.analyticsId}
                  />
                  <ArrowButton
                    direction="right"
                    disabled={!canScrollRight}
                    onClick={() => emblaApi?.scrollNext()}
                    analyticsId={scrollRightButtonMetadata.analyticsId}
                  />
                </div>
              </div>
            )}
          </div>

          <div
            ref={emblaRef}
            className="pointer-fine:overflow-x-auto no-scrollbar overflow-hidden"
          >
            <div className="flex gap-4">
              {categories.map((category) => (
                <div
                  key={category.name}
                  className="border-current/10 xl:min-w-auto relative flex h-96 min-w-72 flex-1 flex-col border p-4"
                >
                  <p className="text-label-lg text-current/50 mb-2">
                    {t({
                      message: plural(category.features.length, {
                        one: "# Update Shipped",
                        other: "# Updates Shipped",
                      }),
                    })}
                  </p>
                  <p className="text-heading-5 mb-4">{category.name}</p>
                  <ul className="no-scrollbar mask-b-from-70% flex-1 overflow-y-auto pb-12">
                    {category.features.map((feature) => (
                      <li
                        key={feature}
                        className="text-body-sm border-current/10 border-t py-2"
                        title={feature}
                      >
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {testimonial && <div className="col-span-full">{testimonial}</div>}
      </div>
    </Section>
  );
}
